import React, { useCallback, useState } from 'react';
import { CreationTaskDrawer, TaskDetailsDrawer } from 'features/daily-plan';
import { buildMultiplePhonesActionsRenderer, extractObjectFromDataTable } from 'hooks/datatable';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import ProfileTableLayout from 'UI/components/organisms/inventoryProfiles/ProfileTableLayout';
import { RowActions } from 'UI/constants/defaults';
import { ENTITY_TYPE_ID_MAP, TabKeys } from 'UI/constants/entityTypes';
import { DataTableLink } from 'UI/utils/renderers';

import { TASK_COLUMNS } from './TasksTab.constants';

const STRINGS = strings.inventoryProfiles.emptyStates.actionPlan;

const ContactRoutes = {
  [ENTITY_TYPE_ID_MAP.candidate]: EntityRoutes.CandidateProfile,
  [ENTITY_TYPE_ID_MAP.company]: EntityRoutes.CompanyProfile,
  [ENTITY_TYPE_ID_MAP.hiringAuthority]: EntityRoutes.HiringAuthorityProfile,
  [ENTITY_TYPE_ID_MAP.contact]: EntityRoutes.ContactProfile
};

export const EXTRA_RENDERERS = {
  contactLink: value => {
    if (!value) return null;

    const { id, entityTypeId, fullName } = value;
    const route = ContactRoutes[entityTypeId];

    return id && route ? (
      <DataTableLink id={id} text={fullName} route={route} enableInTabRedirect />
    ) : null;
  }
};

export const TasksTab = ({
  allowedTaskTypes,
  DataTableProps,
  entityType,
  entityTypeId,
  initialColumns,
  itemName,
  profileId,
  profileModule,
  ...rest
}) => {
  const [uiState, setUiState] = useState({
    isDailyPlanTaskDrawerOpen: false,
    shouldRefreshTableData: false,
    taskId: null
  });

  const paramsExtender = useCallback(() => {
    if (entityTypeId === ENTITY_TYPE_ID_MAP.candidate) return { candidateId: profileId };

    if (entityTypeId === ENTITY_TYPE_ID_MAP.jobOrder) return { jobOrderId: profileId };

    return {
      itemIds: `${entityTypeId}-${profileId}`
    };
  }, [entityTypeId, profileId]);

  const getContactInfo = ({ columns, data }) => {
    const { rowData } = data;
    const item = extractObjectFromDataTable(columns, ['id', 'contact'], rowData);
    return {
      name: item?.contact?.fullName,
      entityId: item?.contact?.id,
      entityType: item?.contact?.entityType
    };
  };

  const getSpecificTabColumns = handleAction => [
    buildMultiplePhonesActionsRenderer({
      customName: 'phoneNumbers',
      handleAction,
      columns: TASK_COLUMNS,
      getContactInfo
    })
  ];

  const handleViewTaskDetails = async taskId => {
    setUiState(prev => ({
      ...prev,
      shouldRefreshTableData: false,
      taskId
    }));
  };

  const handleCloseTaskDetailsDrawer = () => {
    setUiState(prev => ({
      ...prev,
      taskId: null
    }));
  };

  const handleFetchedData = useCallback(() => {
    setUiState(prev => ({
      ...prev,
      shouldRefreshTableData: false
    }));
  }, []);

  const finalActions = [
    {
      name: RowActions.Call,
      value: 'phone'
    },
    {
      name: RowActions.Text,
      value: 'phone'
    },
    { name: RowActions.View, callback: handleViewTaskDetails, pickId: true }
  ];

  const handleOpenTaskCreationDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isDailyPlanTaskDrawerOpen: true
    }));
  };

  const refreshTable = () => {
    setUiState(prev => ({
      ...prev,
      shouldRefreshTableData: true
    }));
  };

  const handleCloseTaskCreationDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isDailyPlanTaskDrawerOpen: false
    }));
  };

  const handleOnTaskCreated = () => {
    refreshTable();
    handleCloseTaskCreationDrawer();
  };
  return (
    <>
      <ProfileTableLayout
        actionText={strings.dailyPlan.actionButton}
        customColumnsExtender={getSpecificTabColumns}
        DataTableProps={{ ...DataTableProps, paramsExtender, onFetchedData: handleFetchedData }}
        finalActions={finalActions}
        hasProfileLoaded={!!profileId}
        initialColumns={initialColumns}
        onNewItemClick={handleOpenTaskCreationDrawer}
        profileModule={profileModule}
        shouldRefresh={uiState.shouldRefreshTableData}
        tabKey={TabKeys.Tasks}
        title={STRINGS.title}
        subtitle={STRINGS.subtitle}
        {...rest}
      />

      <When condition={!!uiState.taskId}>
        <TaskDetailsDrawer
          onClose={handleCloseTaskDetailsDrawer}
          taskId={uiState.taskId}
          onTaskUpdated={refreshTable}
        />
      </When>

      <When condition={uiState.isDailyPlanTaskDrawerOpen && !!entityType}>
        <CreationTaskDrawer
          allowedTaskTypes={allowedTaskTypes}
          entityData={{
            id: profileId,
            name: itemName
          }}
          entityType={entityType}
          onTaskCreated={handleOnTaskCreated}
          onClose={handleCloseTaskCreationDrawer}
          shouldRefreshOnClose
        />
      </When>
    </>
  );
};
